import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { SUBDOMAIN_PATTERN } from '../../utils/tenantMappers';

const buildValidationSchema = (isEditMode) =>
  Yup.object().shape({
    name: Yup.string().trim().required('Tenant name is required'),
    email: Yup.string()
      .trim()
      .email('Enter a valid email address')
      .required('Tenant email is required'),
    business_domain: Yup.string()
      .trim()
      .nullable()
      .matches(
        /^(@)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z]{2,})+$/i,
        'Enter a valid domain (e.g. telus.ca)',
      ),
    subdomain: isEditMode
      ? Yup.string().trim()
      : Yup.string()
          .trim()
          .required('Subdomain is required')
          .matches(
            SUBDOMAIN_PATTERN,
            'Subdomain must be lowercase and can only use letters, numbers, and hyphens.',
          ),
  });

const TenantFormModal = ({
  show,
  mode,
  tenant,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const isEditMode = mode === 'edit';

  return (
    <Modal show={show} onHide={onClose} centered>
      <Formik
        enableReinitialize
        initialValues={{
          name: tenant?.name || '',
          email: tenant?.email || '',
          business_domain: tenant?.business_domain || '',
          subdomain: tenant?.subdomain || '',
        }}
        validationSchema={buildValidationSchema(isEditMode)}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <FormikForm>
            <Modal.Header closeButton>
              <Modal.Title>
                {isEditMode ? 'Update Tenant' : 'Create Tenant'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3" controlId="tenant-name">
                <Form.Label>Tenant Name</Form.Label>
                <Form.Control
                  aria-label="tenant-name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={Boolean(touched.name && errors.name)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="tenant-email">
                <Form.Label>Tenant Email</Form.Label>
                <Form.Control
                  aria-label="tenant-email"
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={Boolean(touched.email && errors.email)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="tenant-business-domain">
                <Form.Label>Business Domain</Form.Label>
                <Form.Control
                  aria-label="tenant-business-domain"
                  name="business_domain"
                  placeholder="e.g. telus.ca"
                  value={values.business_domain}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={Boolean(
                    touched.business_domain && errors.business_domain,
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.business_domain}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  When set, only users whose email matches this domain may sign
                  in to this tenant.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-0" controlId="tenant-subdomain">
                <Form.Label>Subdomain</Form.Label>
                <Form.Control
                  aria-label="tenant-subdomain"
                  name="subdomain"
                  value={values.subdomain}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={Boolean(touched.subdomain && errors.subdomain)}
                  disabled={isEditMode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.subdomain}
                </Form.Control.Feedback>
                {isEditMode ? (
                  <Form.Text className="text-muted">
                    Subdomain is immutable after tenant creation.
                  </Form.Text>
                ) : null}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Tenant'
                  : 'Create Tenant'}
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

TenantFormModal.propTypes = {
  show: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit']),
  tenant: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default TenantFormModal;
